import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { map, exhaustMap, catchError, finalize, switchMap } from 'rxjs/operators';

import { AuthActions } from '../_actions/auth.actions';
import { AuthService } from '../../_services/auth.service';
import { AppState } from '..';
import { select, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  LayoutUtilsServices,
  MessageType,
} from '../../_services/layout-utils.services';
import { LayoutActions } from '../_actions/layout.actions';
import { SalesmanActions } from '../_actions/salesman.actions';
import { SalesmenService } from '../../_services/salesmen.service';
import { SalesService } from '../../_services/sales.service';
import { PrizeRequestsService } from '../../_services/prize-requests.service';
import { currentSalesmenFilters } from '../_selectors/salesman.selectors';

@Injectable()
export class SalesmanEffects {


  loadSalesmen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.loadSalesmen),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._salesmanService.findAll(action.filters).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => SalesmanActions.loadSalesmenSuccess({ items: res.data, total: res.total })),
          catchError(() => EMPTY)
        );
      })
    )
  );

  loadSalesman$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.loadSalesman),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._salesmanService.findOne(action.id).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((data) => {
            return SalesmanActions.loadSalesmanSuccess({ data })
          }),
          catchError(() => EMPTY)
        );
      })
    )
  );




  editSalesman$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.editSalesman),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._salesmanService.update(action.id, action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha actualizado el registro exitosamente',
              type: MessageType.Susses,
            });
            this.router.navigate(['/salesman']);
            this.store.dispatch(
              SalesmanActions.loadSalesmen({ filters: this.filters })
            );
            return SalesmanActions.editSalesmanSuccess({ id: action.id, data: res });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al actualizar el registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  removeSalesman$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.removeSalesman),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._salesmanService.remove(action.id).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha eliminado el registro exitosamente',
              type: MessageType.Susses,
            });
            this.store.dispatch(
              SalesmanActions.loadSalesmen({ filters: this.filters })
            );
            return SalesmanActions.removeSalesmanSuccess({ id: action.id });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al eliminar el registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  DownloadSalesmen$ = createEffect(() => this.actions$.pipe(
    ofType(SalesmanActions.downloadSalesmen),
    exhaustMap((action) => {
      return this._salesmanService.export(action.filters, 'salesmen')
    }),
  ), {
    dispatch: false,
  });

  // NUEVO: Effect para cargar saldo de puntos
  loadCurrentPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.loadCurrentPoints),
      switchMap(({ id }) =>
        this._salesmanService.getCurrentPoints(id).pipe(
          map((response) =>
            SalesmanActions.loadCurrentPointsSuccess({
              points: response.current_points,
            })
          ),
          catchError((error) => {
            this._layoutUtilsServices.showNotification({
              message: error?.error?.message || 'Error al cargar saldo de puntos',
              type: MessageType.Danger,
            });
            return of(SalesmanActions.loadCurrentPointsFailure({ error }));
          })
        )
      )
    )
  );

  // NUEVO: Effect para cargar ventas
  loadVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.loadVehicles),
      switchMap(({ filters }) =>
        this._salesService.findAll(filters).pipe(
          map((response) =>
            SalesmanActions.loadVehiclesSuccess({
              vehicles: response.data,
              total: response.total,
            })
          ),
          catchError((error) => {
            this._layoutUtilsServices.showNotification({
              message: error?.error?.message || 'Error al cargar ventas',
              type: MessageType.Danger,
            });
            return of(SalesmanActions.loadVehiclesFailure({ error }));
          })
        )
      )
    )
  );

  // NUEVO: Effect para exportar ventas
  exportVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.exportVehicles),
      switchMap(({ filters }) =>
        this._salesService.export(filters, 'ventas').pipe(
          map(() => {
            this._layoutUtilsServices.showNotification({
              message: 'Ventas exportadas exitosamente',
              type: MessageType.Susses,
            });
            return SalesmanActions.exportVehiclesSuccess();
          }),
          catchError((error) => {
            this._layoutUtilsServices.showNotification({
              message: error?.error?.message || 'Error al exportar ventas',
              type: MessageType.Danger,
            });
            return of(SalesmanActions.exportVehiclesFailure({ error }));
          })
        )
      )
    )
  );

  // NUEVO: Effect para cargar canjes
  loadPrizeRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.loadPrizeRequests),
      switchMap(({ filters }) =>
        this._prizeRequestsService.findAll(filters).pipe(
          map((response) =>
            SalesmanActions.loadPrizeRequestsSuccess({
              prizeRequests: response.data,
              total: response.total,
            })
          ),
          catchError((error) => {
            this._layoutUtilsServices.showNotification({
              message: error?.error?.message || 'Error al cargar canjes',
              type: MessageType.Danger,
            });
            return of(SalesmanActions.loadPrizeRequestsFailure({ error }));
          })
        )
      )
    )
  );

  // NUEVO: Effect para exportar canjes
  exportPrizeRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesmanActions.exportPrizeRequests),
      switchMap(({ filters }) =>
        this._prizeRequestsService.export(filters, 'canjes').pipe(
          map(() => {
            this._layoutUtilsServices.showNotification({
              message: 'Canjes exportados exitosamente',
              type: MessageType.Susses,
            });
            return SalesmanActions.exportPrizeRequestsSuccess();
          }),
          catchError((error) => {
            this._layoutUtilsServices.showNotification({
              message: error?.error?.message || 'Error al exportar canjes',
              type: MessageType.Danger,
            });
            return of(SalesmanActions.exportPrizeRequestsFailure({ error }));
          })
        )
      )
    )
  );

  filters: any = {};

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private _salesmanService: SalesmenService,
    private _salesService: SalesService,
    private _prizeRequestsService: PrizeRequestsService,
    private _layoutUtilsServices: LayoutUtilsServices,
    private router: Router
  ) {

    this.store
      .pipe(select(currentSalesmenFilters))
      .subscribe((data) => (this.filters = data));
  }
}
